import { useEffect, useState } from 'react';
import './App.css';
import { Menu, X, Sparkles, Bot, TrendingUp, Shield, Zap, Users, Mail, ChevronRight, Check } from 'lucide-react';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="app-container">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"></div>
        <div className="stars"></div>
        <div className="grid-bg"></div>
        <div className="gradient-orb gradient-orb-1"></div>
        <div className="gradient-orb gradient-orb-2"></div>
        <div className="gradient-orb gradient-orb-3"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              <div className="logo-icon">
                <Sparkles className="w-8 h-8" />
              </div>
              <span className="text-2xl font-bold gradient-text">ENOVA AI</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="nav-link">Home</button>
              <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
              <button onClick={() => scrollToSection('packages')} className="nav-link">Packages</button>
              <button onClick={() => scrollToSection('why-us')} className="nav-link">Why Us</button>
              <button onClick={() => scrollToSection('contact')} className="glow-button">
                Contact Us
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mobile-menu">
            <div className="px-4 pt-2 pb-6 space-y-3">
              <button onClick={() => scrollToSection('home')} className="mobile-nav-link">Home</button>
              <button onClick={() => scrollToSection('services')} className="mobile-nav-link">Services</button>
              <button onClick={() => scrollToSection('packages')} className="mobile-nav-link">Packages</button>
              <button onClick={() => scrollToSection('why-us')} className="mobile-nav-link">Why Us</button>
              <button onClick={() => scrollToSection('contact')} className="mobile-nav-link">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="floating animate-float">
            <div className="hero-icon-wrapper mb-8">
              <Bot className="w-20 h-20 mx-auto text-cyan-400" />
            </div>
          </div>
          <h1 className="hero-title mb-6 animate-fade-in-up">
            Welcome to <span className="gradient-text-animated">ENOVA AI</span>
          </h1>
          <p className="hero-subtitle mb-8 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            Empowering Businesses with Next-Gen Artificial Intelligence
          </p>
          <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            At ENova AI, we help businesses save time, cut costs, and scale faster with cutting-edge AI solutions. 
            From startups to enterprises, our tailored AI packages drive growth, automate operations, and deliver 
            real-time insights across industries like Retail, Finance, Healthcare, Real Estate, and Education.
          </p>
          <button 
            onClick={() => scrollToSection('packages')} 
            className="glow-button-large animate-fade-in-up"
            style={{animationDelay: '0.6s'}}
          >
            Explore Our Solutions
            <ChevronRight className="w-5 h-5 ml-2 inline" />
          </button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="section-title text-center mb-16">
            What We <span className="gradient-text">Offer</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Bot, title: 'AI Automation & Chatbots', desc: 'Web, WhatsApp, Facebook integration' },
              { icon: TrendingUp, title: 'Predictive Analytics', desc: 'Real-Time Dashboards & Insights' },
              { icon: Zap, title: 'AI-Driven Marketing', desc: 'E-commerce Tools & Optimization' },
              { icon: Shield, title: 'Document Automation', desc: 'Workflow & Process Automation' },
              { icon: Sparkles, title: 'Custom AI Development', desc: 'NLP, Computer Vision, GenAI Apps' },
              { icon: Users, title: 'Seamless Integration', desc: 'Salesforce, HubSpot, Shopify, Zoho' },
            ].map((service, index) => (
              <div key={index} className="glass-card service-card" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="service-icon-wrapper mb-4">
                  <service.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">{service.title}</h3>
                <p className="text-gray-400">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="section-title text-center mb-16">
            Our <span className="gradient-text">Packages</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Bronze Package */}
            <div className="package-card package-bronze">
              <div className="package-badge">BRONZE</div>
              <div className="package-content">
                <ul className="space-y-4 mb-8">
                  {[
                    'AI Chatbot (Website + WhatsApp/Facebook)',
                    'Automated lead capture & FAQs',
                    'Basic AI Analytics Dashboard',
                    'Social Media AI Assistant',
                    'Monthly Performance Report'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 mr-3 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => scrollToSection('contact')} className="package-button">
                  Get Started
                </button>
              </div>
            </div>

            {/* Silver Package */}
            <div className="package-card package-silver">
              <div className="package-badge-popular">MOST POPULAR</div>
              <div className="package-badge">SILVER</div>
              <div className="package-content">
                <ul className="space-y-4 mb-8">
                  {[
                    'Advanced AI Chatbot with Sales Funnel',
                    'AI Marketing Automation (Email + Segmentation)',
                    'E-commerce AI Tools (Recommendations + Stock)',
                    'AI Document Automation (Contracts, Invoices)',
                    'Weekly Performance Insights'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => scrollToSection('contact')} className="package-button">
                  Get Started
                </button>
              </div>
            </div>

            {/* Gold Package */}
            <div className="package-card package-gold">
              <div className="package-badge">GOLD</div>
              <div className="package-content">
                <ul className="space-y-4 mb-8">
                  {[
                    'AI Virtual Assistant (Voice + Text)',
                    'Predictive Analytics (Sales, Demand, Churn)',
                    'AI Fraud Detection & Risk Monitoring',
                    'Tailored Real-Time AI Dashboards',
                    'Dedicated AI Consultant & Priority Support',
                    'Staff Training on AI Adoption'
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check className="w-5 h-5 mr-3 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => scrollToSection('contact')} className="package-button">
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="section-title text-center mb-16">
            Why <span className="gradient-text">ENova AI?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Free AI Audit', desc: 'Strategy in 7 days', icon: Sparkles },
              { title: 'UK-based Experts', desc: 'Dedicated AI Specialists', icon: Users },
              { title: 'End-to-end Support', desc: 'Staff training & consulting', icon: Shield },
            ].map((item, index) => (
              <div key={index} className="glass-card why-card text-center" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="why-icon-wrapper mx-auto mb-6">
                  <item.icon className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{item.title}</h3>
                <p className="text-gray-400 text-lg">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="section-title text-center mb-16">
            Get in <span className="gradient-text">Touch</span>
          </h2>
          <div className="glass-card p-8">
            <p className="text-center text-gray-300 text-lg mb-8">
              Ready to transform your business with AI? Contact us today for a free consultation!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:enovaaitechnologies@gmail.com" 
                className="glow-button-large flex items-center"
              >
                <Mail className="w-5 h-5 mr-2" />
                enovaaitechnologies@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>&copy; 2025 ENOVA AI Technologies. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;